import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

const DEFAULT_COPY = {
    vehicleModels: 'Vehicle models',
    contentLabel: 'Filter operating analysis by vehicle model',
    allModels: 'All models',
    selectedCount: '{count} selected',
    vehicleCount: '{count} vehicles',
    minimumSelection: 'Select at least one vehicle model.',
    apply: 'Apply',
    cancel: 'Cancel',
    close: 'Close',
};

const formatTemplate = (template, values = {}) => (
    String(template || '').replace(/\{(\w+)\}/g, (match, key) => (
        values[key] !== undefined ? values[key] : match
    ))
);

const normalizeValues = (values = []) => (
    [...new Set((values || []).map((value) => String(value || '').trim()).filter(Boolean))]
);

const getDraftValues = (options, appliedModels) => {
    const optionValues = options.map((option) => option.value);
    const appliedValues = normalizeValues(appliedModels);
    if (appliedValues.length === 0) return optionValues;

    const optionSet = new Set(optionValues);
    const availableAppliedValues = appliedValues.filter((value) => optionSet.has(value));
    return availableAppliedValues.length > 0 ? availableAppliedValues : optionValues;
};

const haveSameValues = (left, right) => {
    if (left.length !== right.length) return false;
    const rightSet = new Set(right);
    return left.every((value) => rightSet.has(value));
};

export default function VehicleModelFilter({
    filter,
    appliedModels = [],
    onApply,
    isBusy = false,
    copy = {},
    locale = 'en-US',
    isRtl = false,
}) {
    const resolvedCopy = { ...DEFAULT_COPY, ...copy };
    const options = useMemo(() => filter?.options || [], [filter?.options]);
    const isAvailable = Boolean(filter?.isAvailable) && options.length > 1;
    const popupId = useId();
    const triggerRef = useRef(null);
    const popupRef = useRef(null);
    const firstCheckboxRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [draftValues, setDraftValues] = useState(() => getDraftValues(options, appliedModels));

    const optionValues = useMemo(() => options.map((option) => option.value), [options]);
    const effectiveAppliedValues = useMemo(
        () => getDraftValues(options, appliedModels),
        [appliedModels, options]
    );
    const allSelected = draftValues.length === optionValues.length
        && optionValues.every((value) => draftValues.includes(value));
    const hasAppliedSubset = appliedModels.length > 0
        && appliedModels.length < optionValues.length;
    const isDirty = !haveSameValues(draftValues, effectiveAppliedValues);

    const close = useCallback((restoreFocus = false) => {
        setDraftValues(getDraftValues(options, appliedModels));
        setIsOpen(false);
        if (restoreFocus) {
            triggerRef.current?.focus();
        }
    }, [appliedModels, options]);

    const open = () => {
        setDraftValues(getDraftValues(options, appliedModels));
        setIsOpen(true);
    };

    useEffect(() => {
        if (!isOpen) {
            setDraftValues(getDraftValues(options, appliedModels));
            return undefined;
        }

        firstCheckboxRef.current?.focus();
        const handlePointerOutside = (event) => {
            if (
                !popupRef.current?.contains(event.target)
                && !triggerRef.current?.contains(event.target)
            ) {
                close();
            }
        };
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                close(true);
            }
        };

        document.addEventListener('mousedown', handlePointerOutside);
        document.addEventListener('touchstart', handlePointerOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handlePointerOutside);
            document.removeEventListener('touchstart', handlePointerOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [appliedModels, close, isOpen, options]);

    if (!isAvailable) return null;

    const toggleAll = () => {
        setDraftValues(allSelected ? [] : optionValues);
    };

    const toggleOption = (value) => {
        setDraftValues((currentValues) => (
            currentValues.includes(value)
                ? currentValues.filter((currentValue) => currentValue !== value)
                : [...currentValues, value]
        ));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (draftValues.length === 0 || !isDirty || isBusy) return;

        const nextAppliedModels = allSelected ? [] : optionValues.filter((value) => draftValues.includes(value));
        onApply(nextAppliedModels);
        setIsOpen(false);
        triggerRef.current?.focus();
    };

    return (
        <div className="operating-analysis-model-filter" dir={isRtl ? 'rtl' : 'ltr'}>
            <button
                ref={triggerRef}
                type="button"
                className={`operating-analysis-model-filter-trigger${hasAppliedSubset ? ' active' : ''}`}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-controls={isOpen ? popupId : undefined}
                disabled={isBusy}
                onClick={() => (isOpen ? close() : open())}
            >
                <i className="fas fa-filter" aria-hidden="true"></i>
                <span>{resolvedCopy.vehicleModels}</span>
                {hasAppliedSubset ? (
                    <span
                        className="operating-analysis-model-filter-count"
                        aria-label={formatTemplate(resolvedCopy.selectedCount, {
                            count: Number(appliedModels.length).toLocaleString(locale),
                        })}
                    >
                        {Number(appliedModels.length).toLocaleString(locale)}
                    </span>
                ) : null}
                <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} aria-hidden="true"></i>
            </button>

            {isOpen ? (
                <form
                    ref={popupRef}
                    id={popupId}
                    className="operating-analysis-model-filter-popover"
                    role="dialog"
                    aria-modal="false"
                    aria-label={resolvedCopy.contentLabel}
                    dir={isRtl ? 'rtl' : 'ltr'}
                    onSubmit={handleSubmit}
                >
                    <div className="operating-analysis-model-filter-header">
                        <strong>{resolvedCopy.vehicleModels}</strong>
                        <button
                            type="button"
                            className="operating-analysis-model-filter-close"
                            aria-label={resolvedCopy.close}
                            onClick={() => close(true)}
                        >
                            <i className="fas fa-times" aria-hidden="true"></i>
                        </button>
                    </div>

                    <div className="operating-analysis-model-filter-options">
                        <label className="operating-analysis-model-filter-option all-models">
                            <input
                                ref={firstCheckboxRef}
                                type="checkbox"
                                checked={allSelected}
                                onChange={toggleAll}
                            />
                            <span>{resolvedCopy.allModels}</span>
                            <small>
                                {formatTemplate(resolvedCopy.vehicleCount, {
                                    count: Number(filter.totalVehicleCount || 0).toLocaleString(locale),
                                })}
                            </small>
                        </label>

                        {options.map((option) => (
                            <label className="operating-analysis-model-filter-option" key={option.value}>
                                <input
                                    type="checkbox"
                                    checked={draftValues.includes(option.value)}
                                    onChange={() => toggleOption(option.value)}
                                />
                                <span>{option.label || option.value}</span>
                                <small>
                                    {formatTemplate(resolvedCopy.vehicleCount, {
                                        count: Number(option.vehicleCount || 0).toLocaleString(locale),
                                    })}
                                </small>
                            </label>
                        ))}
                    </div>

                    {draftValues.length === 0 ? (
                        <p className="operating-analysis-model-filter-validation" role="alert">
                            {resolvedCopy.minimumSelection}
                        </p>
                    ) : null}

                    <div className="operating-analysis-model-filter-actions">
                        <button type="button" className="operating-analysis-secondary" onClick={() => close(true)}>
                            {resolvedCopy.cancel}
                        </button>
                        <button
                            type="submit"
                            className="operating-analysis-primary"
                            disabled={draftValues.length === 0 || !isDirty || isBusy}
                        >
                            {resolvedCopy.apply}
                        </button>
                    </div>
                </form>
            ) : null}
        </div>
    );
}
